import type { AdminAuthUser, AdminSession } from '@duvamarine/shared';
import { create } from 'zustand';

export interface TwoFactorChallenge {
  token: string;
  step: 'setup' | 'verify';
  expiresAt: number;
  email: string;
}

interface AdminAuthState {
  status: 'unknown' | 'authenticated' | 'anonymous';
  /** Memory only. The refresh token is an httpOnly cookie scoped to /api/v1/admin/auth. */
  accessToken: string | null;
  user: AdminAuthUser | null;
  /** Pending second factor after a correct password (never persisted). */
  challenge: TwoFactorChallenge | null;
  setSession: (session: AdminSession) => void;
  setChallenge: (challenge: TwoFactorChallenge | null) => void;
  clear: () => void;
}

/**
 * Non-secret hint that this browser holds an admin refresh cookie (the cookie itself is httpOnly):
 * without it the console skips the session refresh on load (no request, no 401 in the console).
 */
const SESSION_HINT_KEY = 'dm.admin.session';

function writeSessionHint(signedIn: boolean): void {
  try {
    if (signedIn) window.localStorage.setItem(SESSION_HINT_KEY, '1');
    else window.localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // Storage blocked: the refresh is simply attempted on every load.
  }
}

export function hasSessionHint(): boolean {
  try {
    return window.localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    return true;
  }
}

export const useAdminAuthStore = create<AdminAuthState>()((set) => ({
  status: 'unknown',
  accessToken: null,
  user: null,
  challenge: null,
  setSession: (session) => {
    writeSessionHint(true);
    set({
      status: 'authenticated',
      accessToken: session.accessToken,
      user: session.user,
      challenge: null,
    });
  },
  setChallenge: (challenge) => set({ challenge }),
  clear: () => {
    writeSessionHint(false);
    set({ status: 'anonymous', accessToken: null, user: null, challenge: null });
  },
}));
